import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Hourglass, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DeleteListItemDialog } from '@/components/lists/DeleteListItemDialog';
import type { ProjectCardItem } from '@/components/ProjectCard';
import { useDeleteProject } from '@/hooks/use-delete-project';
import { formatDuration } from '@/lib/format';
import { formatAiredDay } from '@/lib/format/airedDay';

interface Props {
  item: ProjectCardItem;
  type: 'project' | 'library';
  onDeleted?: (id: string) => void;
}

/**
 * An expired video: a compact grey row instead of a full-size faded card, so
 * the 60–100 expired videos of a long-time user stay out of the way. Nothing
 * opens (the files are gone); a project can still be deleted.
 */
export function ListExpiredRow({ item, type, onDeleted }: Props) {
  const { t, i18n } = useTranslation();
  const [thumbnailError, setThumbnailError] = useState(false);
  const channel = item.youtube_channels?.[0]?.name?.split(' | ')[0]?.trim();
  const meta = [
    channel,
    formatAiredDay(item.createdAt, i18n.language),
    item.duration_seconds ? formatDuration(item.duration_seconds) : null,
  ].filter(Boolean).join(' · ');

  return (
    <div data-testid="list-expired-row" className="flex items-center gap-3 border-b border-border py-2 last:border-b-0">
      <div className="relative h-12 w-[85px] shrink-0 overflow-hidden rounded-md bg-black sm:h-[63px] sm:w-28">
        {item.thumbnail_url && !thumbnailError ? (
          <img
            src={item.thumbnail_url}
            alt=""
            className="h-full w-full object-cover opacity-50 grayscale"
            onError={() => setThumbnailError(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-secondary text-[10px] text-muted-foreground">
            {t('projectCard.noPreview')}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-foreground/80 sm:text-sm">{item.name}</p>
        {meta && <p className="mt-0.5 truncate text-xs text-muted-foreground">{meta}</p>}
      </div>
      <span className="hidden shrink-0 items-center gap-1 rounded-full border border-amber-500/40 px-2 py-0.5 text-[11px] text-amber-400 sm:inline-flex">
        <Hourglass className="h-3 w-3" />
        {t('lists.expired')}
      </span>
      {type === 'project' && !item.public && (
        <DeleteExpiredProject id={item.id} onDeleted={onDeleted} />
      )}
    </div>
  );
}

/** Projects can be deleted (libraries cannot), so only their rows load the hook. */
function DeleteExpiredProject({ id, onDeleted }: { id: string; onDeleted?: (id: string) => void }) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { deleteProject, loading: deleting } = useDeleteProject({ onSuccess: () => onDeleted?.(id) });
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid="list-expired-delete"
        aria-label={t('projectCard.removeProject')}
        className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-destructive"
        onClick={() => setConfirmOpen(true)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      <DeleteListItemDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        deleting={deleting}
        warnAboutClips
        onConfirm={async () => {
          await deleteProject(id);
          setConfirmOpen(false);
        }}
      />
    </>
  );
}
